import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { createBook, type CreateBookResult } from "../api/client";
import { ApiError, type Book, type CreateBookOptions } from "../api/types";
import { looksLikeYoutubeUrl } from "../lib/youtube";
import CreatePaperDialog from "./CreatePaperDialog";
import { SpinnerIcon } from "./Icon";

interface UrlInputProps {
  /** Called after a successful create — `created` is false when the server returned an existing duplicate. */
  onCreated?: (book: Book, created: boolean) => void;
  autoFocus?: boolean;
}

export default function UrlInput({ onCreated, autoFocus }: UrlInputProps) {
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const mutation = useMutation<CreateBookResult, ApiError, { url: string; options: CreateBookOptions }>({
    mutationFn: ({ url, options }) => createBook(url, options),
    onSuccess: ({ book, created }) => {
      setDialogOpen(false);
      onCreated?.(book, created);
      if (created) {
        setValue("");
      } else {
        // Duplicate submission (§3.1 idempotency) — jump straight to the existing book.
        navigate(`/books/${book.id}`);
      }
    },
    onError: () => setDialogOpen(false),
  });

  // Submitting the URL opens the per-paper options dialog; the create request
  // only fires once those are confirmed.
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    const trimmed = value.trim();
    if (!trimmed) return;
    if (!looksLikeYoutubeUrl(trimmed)) {
      setLocalError("That doesn't look like a YouTube video link. Try a youtube.com/watch?v=… or youtu.be/… URL.");
      return;
    }
    mutation.reset();
    setDialogOpen(true);
  }

  const error = localError ?? (mutation.error ? mutation.error.message : null);
  const hint = localError ? null : mutation.error?.hint ?? null;

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="url"
          inputMode="url"
          autoFocus={autoFocus}
          placeholder="Paste a YouTube link…"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setLocalError(null);
            if (mutation.isError) mutation.reset();
          }}
          className="w-full flex-1 rounded-md border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-muted"
          aria-label="YouTube video URL"
          aria-invalid={!!error}
        />
        <button
          type="submit"
          disabled={mutation.isPending || !value.trim()}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-accent px-5 py-3 text-base font-medium text-white transition-colors hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? <SpinnerIcon width={16} height={16} /> : null}
          {mutation.isPending ? "Adding…" : "Create a paper"}
        </button>
      </div>
      {error ? (
        <p className="mt-2 text-sm text-danger" role="alert">
          {error}
          {hint ? ` - ${hint}` : ""}
        </p>
      ) : null}
      </form>
      {/* Outside the URL form — Modal renders inline and forms must not nest. */}
      <CreatePaperDialog
        open={dialogOpen}
        pending={mutation.isPending}
        onClose={() => setDialogOpen(false)}
        onConfirm={(options) => mutation.mutate({ url: value.trim(), options })}
      />
    </div>
  );
}
